import React, { useState, useRef } from 'react';
import { ClockIcon, TruckIcon, UserIcon, CheckCircleIcon, XIcon, QrCodeIcon, PenToolIcon, AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
interface JobCompletionModalProps {
  jobId: string;
  onComplete: (jobId: string, completionData: any) => void;
  onCancel: () => void;
}
export const JobCompletionModal = ({
  jobId,
  onComplete,
  onCancel
}: JobCompletionModalProps) => {
  const [truckHours, setTruckHours] = useState('');
  const [foremanName, setForemanName] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<'signature' | 'qrcode' | null>('signature');
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrScanned, setQrScanned] = useState(false);
  const [qrData, setQrData] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [skipVerification, setSkipVerification] = useState(false);
  const signatureRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  // Handle signature drawing
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!signatureRef.current) return;
    const canvas = signatureRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !signatureRef.current) return;
    const canvas = signatureRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      // Prevent scrolling when drawing signature on touch devices
      e.preventDefault();
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const rect = canvas.getBoundingClientRect();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };
  const stopDrawing = () => {
    if (!isDrawing || !signatureRef.current) return;
    setIsDrawing(false);
    setHasSignature(true);
    // Save signature as data URL
    const canvas = signatureRef.current;
    setSignature(canvas.toDataURL());
  };
  const clearSignature = () => {
    if (!signatureRef.current) return;
    const canvas = signatureRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignature(null);
  };
  // Handle QR code scanning
  const handleScanQrCode = () => {
    setVerificationMethod('qrcode');
    setShowQrScanner(true);
    // In a real app, this would activate the device camera and scan a QR code
    // For demo purposes, we'll simulate a successful scan after a delay
    setTimeout(() => {
      const mockForemanData = {
        name: 'James Wilson',
        id: 'FRM-2023-089',
        company: 'ABC Construction'
      };
      setQrData(JSON.stringify(mockForemanData));
      setForemanName(mockForemanData.name);
      setQrScanned(true);
      setShowQrScanner(false);
    }, 2000);
  };
  const handleSubmit = () => {
    // Validate required fields - truck hours are optional
    if (!skipVerification && verificationMethod === 'signature' && !hasSignature && !foremanName) {
      alert('Please have the foreman sign or enter foreman name');
      return;
    }
    // Prepare completion data
    const completionData = {
      truckHours: truckHours ? parseFloat(truckHours) : null,
      foremanVerification: {
        method: skipVerification ? 'skipped' : verificationMethod,
        foremanName,
        signature: signature,
        qrData: qrData,
        timestamp: new Date().toISOString(),
        skipped: skipVerification
      }
    };
    // Call the onComplete callback with the job ID and completion data
    onComplete(jobId, completionData);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2422">
      <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl" data-id="element-2423">
        <div className="p-4 border-b flex justify-between items-center" data-id="element-2424">
          <h3 className="font-bold text-lg" data-id="element-2425">Complete Job</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" data-id="element-2426">
            <XIcon size={20} data-id="element-2427" />
          </button>
        </div>
        <div className="p-4 max-h-[80vh] overflow-y-auto" data-id="element-2428">
          {/* Truck Hours Section */}
          <div className="mb-6" data-id="element-2429">
            <div className="flex items-center gap-2 mb-2" data-id="element-2430">
              <ClockIcon size={20} className="text-blue-600" data-id="element-2431" />
              <h4 className="font-medium" data-id="element-2432">Truck Hours</h4>
            </div>
            <p className="text-sm text-gray-600 mb-2" data-id="element-2433">
              Please record the truck hours if your company tracks this metric
              for maintenance and billing purposes.
            </p>
            <div className="flex items-center" data-id="element-2434">
              <input type="number" value={truckHours} onChange={e => setTruckHours(e.target.value)} placeholder="Enter truck hours" className="flex-1 border border-gray-300 rounded-l-md p-2" step="0.1" data-id="element-2435" />
              <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm" data-id="element-2436">
                hours
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-2437">(Optional)</p>
          </div>
          {/* Foreman Verification Section */}
          <div className="mb-6" data-id="element-2438">
            <div className="flex items-center gap-2 mb-2" data-id="element-2439">
              <UserIcon size={20} className="text-blue-600" data-id="element-2440" />
              <h4 className="font-medium" data-id="element-2441">Foreman Verification</h4>
            </div>
            {/* Foreman Name Input */}
            <div className="mb-3" data-id="element-2442">
              <label className="block text-sm text-gray-600 mb-1" data-id="element-2443">
                Foreman Name (Optional)
              </label>
              <input type="text" value={foremanName} onChange={e => setForemanName(e.target.value)} placeholder="Enter foreman name" className="w-full border border-gray-300 rounded-md p-2" data-id="element-2444" />
            </div>
            {/* Verification Method Selection */}
            <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-2445">
              <button onClick={() => setVerificationMethod('signature')} className={`py-2 px-4 rounded-md flex items-center justify-center gap-2 ${verificationMethod === 'signature' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 border border-gray-300'}`} data-id="element-2446">
                <PenToolIcon size={18} data-id="element-2447" />
                Signature
              </button>
              <button onClick={handleScanQrCode} className={`py-2 px-4 rounded-md flex items-center justify-center gap-2 ${verificationMethod === 'qrcode' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 border border-gray-300'}`} data-id="element-2448">
                <QrCodeIcon size={18} data-id="element-2449" />
                Scan QR Code
              </button>
            </div>
            {/* Signature Canvas */}
            {verificationMethod === 'signature' && !qrScanned && <div className="mb-3" data-id="element-2450">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-2451">
                  Signature
                </label>
                <div className="border border-gray-300 rounded-md bg-gray-50 p-1" data-id="element-2452">
                  <canvas ref={signatureRef} width={468} height={200} className="w-full bg-white border border-gray-200 rounded touch-none" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} data-id="element-2453" />
                  <div className="flex justify-end mt-1" data-id="element-2454">
                    <button onClick={clearSignature} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-2455">
                      Clear Signature
                    </button>
                  </div>
                </div>
              </div>}
            {/* QR Scanner */}
            {showQrScanner && <div className="mb-3 bg-gray-100 p-4 rounded-md text-center" data-id="element-2456">
                <div className="animate-pulse mb-2" data-id="element-2457">
                  <QrCodeIcon size={64} className="mx-auto text-blue-600" data-id="element-2458" />
                </div>
                <p className="text-sm" data-id="element-2459">Scanning QR code...</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2460">
                  Position the QR code within the camera view
                </p>
              </div>}
            {/* QR Scan Result */}
            {qrScanned && <div className="mb-3 bg-green-50 p-4 rounded-md border border-green-200" data-id="element-2461">
                <div className="flex items-center gap-2 mb-2" data-id="element-2462">
                  <CheckCircleIcon size={20} className="text-green-600" data-id="element-2463" />
                  <span className="font-medium" data-id="element-2464">
                    QR Code Scanned Successfully
                  </span>
                </div>
                <p className="text-sm" data-id="element-2465">
                  Foreman: <span className="font-medium" data-id="element-2466">{foremanName}</span>
                </p>
                <button onClick={() => {
              setQrScanned(false);
              setVerificationMethod('signature');
            }} className="text-sm text-blue-600 hover:text-blue-800 mt-2 flex items-center gap-1" data-id="element-2467">
                  <RefreshCwIcon size={14} data-id="element-2468" />
                  Scan Again
                </button>
              </div>}
            {/* Skip Verification Option */}
            <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200 mt-4" data-id="element-2469">
              <div className="flex items-start gap-2" data-id="element-2470">
                <AlertTriangleIcon size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-2471" />
                <div data-id="element-2472">
                  <p className="text-sm text-yellow-800" data-id="element-2473">
                    No foreman available? You can complete the job without a
                    signature, but it will be flagged on your timecard for
                    review.
                  </p>
                  <button onClick={() => setSkipVerification(!skipVerification)} className="mt-2 px-3 py-1 bg-yellow-200 text-yellow-800 rounded-md text-sm font-medium hover:bg-yellow-300" data-id="element-2474">
                    {skipVerification ? '✓ Skip Signature' : 'Skip Signature'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="p-4 border-t bg-gray-50 flex justify-between" data-id="element-2475">
          <button onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-2476">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2" data-id="element-2477">
            <CheckCircleIcon size={18} data-id="element-2478" />
            Complete Job
          </button>
        </div>
      </div>
    </div>;
};